import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Card, Container, Row,
} from 'react-bootstrap';
import { toast } from 'react-toastify';

import CardTable from '../components/tables/CardTable';
import CardForm from '../components/forms/CardForm';
import FormModal from '../components/modals/FormModal';
import ConfirmationModal from '../components/modals/ConfirmationModal';
import ToolbarContainer from './tables/ToolbarContainer';

import {
  addCard, loadCards, updateCard, deleteCard, deactivateCard, activateCard
} from '../actions/creditCards';
import { updatePage, updatePageSize } from '../actions/pagination';

import { Shapes } from '../models/entities';
import CardToolbar from '../components/toolbars/CardToolbar';

class ManageCreditCards extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      modalTitle: '',
      modalError: undefined,
      selectedId: undefined,
      isEditing: true,
      showConfirmDelete: false,
      showConfirmDeactivate: false,
      showConfirmActivate: false,
    };
    this.onAdd = this.onAdd.bind(this);
    this.onEdit = this.onEdit.bind(this);
    this.onSave = this.onSave.bind(this);
    this.onDetails = this.onDetails.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.onDeactivate = this.onDeactivate.bind(this);
    this.onActivate = this.onActivate.bind(this);
    this.onConfirmDelete = this.onConfirmDelete.bind(this);
    this.onConfirmDeactivate = this.onConfirmDeactivate.bind(this);
    this.onConfirmActivate = this.onConfirmActivate.bind(this);
    this.onHideModal = this.onHideModal.bind(this);
    this.onHideConfirm = this.onHideConfirm.bind(this);
    this.onDeleteDone = this.onDeleteDone.bind(this);
    this.onDeactivateDone = this.onDeactivateDone.bind(this);
    this.onActivateDone = this.onActivateDone.bind(this);
  }

  componentDidMount() {
    const { loadCards: loadAllCards } = this.props;
    loadAllCards();
  }

  onAdd() {
    this.setState({
      selectedId: undefined,
      card: undefined,
      showModal: true,
      modalTitle: 'Add New Credit Card',
      isEditing: true,
    });
  }

  onEdit() {
    this.setState({
      showModal: true,
      modalTitle: 'Edit Credit Card',
      isEditing: true,
    });
  }

  onDetails() {
    this.setState({
      showModal: true,
      modalTitle: 'Card Details',
      isEditing: false,
    });
  }

  onSave(card) {
    const { addCard: onAdd, updateCard: onUpdate, loadCards: refreshCards } = this.props;
    const { selectedId } = this.state;

    let promise;
    let action;
    if (selectedId) {
      promise = onUpdate({ ...card, id: selectedId });
      action = 'updated';
    } else {
      promise = onAdd(card);
      action = 'created';
    }

    promise.then(() => refreshCards()).then(() => {
      this.onHideModal();
      toast.success(`Card ${card.alias} ${action} successfully!`);
    }).catch((error) => this.setState({ modalError: error }));
  }

  onDeactivate() {
    this.setState({
      showConfirmDeactivate: true,
    });
  }

  onActivate() {
    this.setState({
      showConfirmActivate: true,
    });
  }

  onDelete() {
    this.setState({
      showConfirmDelete: true,
    });
  }

  onConfirmDelete() {
    const { selectedId } = this.state;
    const { deleteCard: onDelete, loadCards: refreshCards, cardsById } = this.props;

    return onDelete(selectedId).then(() => refreshCards())
      .then(() => toast.success(`Card ${cardsById[selectedId].alias} deleted successfully!`))
      .catch((error) => toast.error(error.message))
      .finally(this.onDeleteDone);
  }

  onConfirmDeactivate() {
    const { selectedId } = this.state;
    const { deactivateCard: onDeactivate, loadCards: refreshCards, cardsById } = this.props;

    return onDeactivate(selectedId).then(() => refreshCards())
      .then(() => toast.success(`Card ${cardsById[selectedId].alias} deactivated successfully!`))
      .catch((error) => toast.error(error.message))
      .finally(this.onDeactivateDone);
  }

  onConfirmActivate() {
    const { selectedId } = this.state;
    const { activateCard: onActivate, loadCards: refreshCards, cardsById } = this.props;

    return onActivate(selectedId).then(() => refreshCards())
      .then(() => toast.success(`Card ${cardsById[selectedId].alias} activated successfully!`))
      .catch((error) => toast.error(error.message))
      .finally(this.onActivateDone);
  }


  onHideModal() {
    this.setState({
      showModal: false,
      modalTitle: '',
      modalError: undefined,
      isEditing: false,
    });
  }

  onDeleteDone() {
    this.setState({
      selectedId: undefined,
      showConfirmDelete: false,
    });
  }

  onDeactivateDone() {
    this.setState({
      showConfirmDeactivate: false,
    });
  }

  onActivateDone() {
    this.setState({
      showConfirmActivate: false,
    });
  }

  onHideConfirm() {
    this.setState({
      showConfirmDelete: false,
      showConfirmDeactivate: false,
      showConfirmActivate: false,
    });
  }

  render() {
    const {
      cards, cardsById, pagination, loadCards: refreshCards,
    } = this.props;

    const {
      showModal, modalError, modalTitle, showConfirmDelete, showConfirmDeactivate, showConfirmActivate, isEditing, selectedId,
    } = this.state;

    const deactivate = selectedId && cardsById[selectedId].active;

    return (
      <Card style={{ height: '100%' }}>
        <Card.Header className="text-center">
          <strong style={{ fontSize: '22px' }}>Manage Credit Cards</strong>
        </Card.Header>
        <Card.Body>
          <Container>
            <Row>
              <ToolbarContainer pagination={pagination} onPaginationChange={refreshCards}>
                <CardToolbar
                  selectedId={selectedId}
                  onAdd={this.onAdd}
                  onEdit={this.onEdit}
                  onDetails={this.onDetails}
                  onDelete={this.onDelete}
                  onDeactivate={deactivate ? this.onDeactivate : undefined}
                  onActivate={!deactivate ? this.onActivate : undefined}
                />
              </ToolbarContainer>
            </Row>
            <Row>
              <CardTable
                cards={cards}
                onSelectChange={(id) => this.setState({ selectedId: id })}
                selectedId={selectedId}
              />
              <FormModal
                show={showModal}
                size="lg"
                title={modalTitle}
                isEditing={isEditing}
                onHide={this.onHideModal}
                formId="cardForm"
                error={modalError}
              >
                <CardForm
                  disabled={!isEditing}
                  card={selectedId ? cardsById[selectedId] : null}
                  formId="cardForm"
                  onSubmit={this.onSave}
                />
              </FormModal>
              <ConfirmationModal
                title="Delete Credit Card"
                message="This is an irreversible operation. Are you sure you want to continue?"
                show={showConfirmDelete}
                onConfirm={this.onConfirmDelete}
                onClose={this.onHideConfirm}
              />
              <ConfirmationModal
                title="Deactivate This Credit Card"
                message="Are you sure you want to deactivate this card?"
                show={showConfirmDeactivate}
                onConfirm={this.onConfirmDeactivate}
                onClose={this.onHideConfirm}
              />
              <ConfirmationModal
                title="Activate This Credit Card"
                message="Are you sure you want to activate this card?"
                show={showConfirmActivate}
                onConfirm={this.onConfirmActivate}
                onClose={this.onHideConfirm}
              />
            </Row>
          </Container>
        </Card.Body>
      </Card>
    );
  }
}

ManageCreditCards.propTypes = {
  addCard: PropTypes.func.isRequired,
  updateCard: PropTypes.func.isRequired,
  deleteCard: PropTypes.func.isRequired,
  deactivateCard: PropTypes.func.isRequired,
  activateCard: PropTypes.func.isRequired,
  loadCards: PropTypes.func.isRequired,
  cards: PropTypes.arrayOf(Shapes.CARD),
  cardsById: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.number,
    alias: PropTypes.string,
    active: PropTypes.bool,
  })),
  pagination: Shapes.PAGINATION,
};

ManageCreditCards.defaultProps = {
  cards: [],
  cardsById: {},
  pagination: null,
};

const mapStateToProps = (state) => {
  const {
    pagination: { cards: pagination },
    entities: { cards },
  } = state;

  return {
    pagination,
    cards: pagination.ids ? pagination.ids.map((id) => cards[id]) : [],
    cardsById: cards,
  };
};

export default connect(mapStateToProps, {
  addCard, updateCard, deleteCard, deactivateCard, activateCard, loadCards, updatePage, updatePageSize,
})(ManageCreditCards);
